import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './Evidence.css';

const STATUS_COLORS = {
  REQUESTED: '#f59e0b',
  SUBMITTED: '#3b82f6',
  APPROVED:  '#10b981',
  REJECTED:  '#ef4444',
};

const REVIEW_COLORS = {
  PENDING:  '#6b7280',
  ACCEPTED: '#10b981',
  REJECTED: '#ef4444',
};

const TYPE_ICONS = { FILE: 'fa-file-alt', NOTE: 'fa-sticky-note', LINK: 'fa-external-link-alt' };

function StatusBadge({ status, colors }) {
  return (
    <span className="ev-badge" style={{ background: colors[status] || '#6b7280' }}>
      {status}
    </span>
  );
}

export default function EvidencePage() {
  const { t } = useTranslation();
  const role      = localStorage.getItem('role');
  const isAdmin   = role === 'ROLE_ADMIN';

  const [requests,   setRequests]   = useState([]);
  const [summary,    setSummary]    = useState({});
  const [activeTab,  setActiveTab]  = useState('my');      // 'my' | 'all'
  const [expanded,   setExpanded]   = useState({});        // requestId → bool
  const [items,      setItems]      = useState({});        // requestId → []
  const [submitting, setSubmitting] = useState(null);      // requestId
  const [noteText,   setNoteText]   = useState('');
  const [linkUrl,    setLinkUrl]    = useState('');
  const [linkNote,   setLinkNote]   = useState('');
  const [submitType, setSubmitType] = useState('NOTE');
  const [reviewModal,setReviewModal]= useState(null);      // { itemId, status, notes }
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  const load = useCallback(async () => {
    try {
      const [reqRes, sumRes] = await Promise.all([
        axiosInstance.get(isAdmin && activeTab === 'all' ? '/evidence/requests' : '/evidence/requests/my'),
        axiosInstance.get('/evidence/summary'),
      ]);
      setRequests(reqRes.data || []);
      setSummary(sumRes.data || {});
    } catch {
      setError(t('EvidencePage.errorLoadRequests'));
    }
  }, [isAdmin, activeTab, t]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (requestId) => {
    const next = { ...expanded, [requestId]: !expanded[requestId] };
    setExpanded(next);
    if (next[requestId] && !items[requestId]) {
      try {
        const res = await axiosInstance.get(`/evidence/requests/${requestId}/items`);
        setItems(i => ({ ...i, [requestId]: res.data }));
      } catch { /* silent */ }
    }
  };

  const handleNoteSubmit = async (requestId) => {
    if (!noteText.trim()) return;
    try {
      await axiosInstance.post(`/evidence/requests/${requestId}/items/note`, { notes: noteText });
      setNoteText(''); setSubmitting(null); setSuccess(t('EvidencePage.successNoteSubmitted'));
      const res = await axiosInstance.get(`/evidence/requests/${requestId}/items`);
      setItems(i => ({ ...i, [requestId]: res.data }));
      load();
    } catch { setError(t('EvidencePage.errorSubmitNote')); }
  };

  const handleLinkSubmit = async (requestId) => {
    if (!linkUrl.trim()) return;
    try {
      await axiosInstance.post(`/evidence/requests/${requestId}/items/link`,
        { externalLink: linkUrl, notes: linkNote });
      setLinkUrl(''); setLinkNote(''); setSubmitting(null); setSuccess(t('EvidencePage.successLinkSubmitted'));
      const res = await axiosInstance.get(`/evidence/requests/${requestId}/items`);
      setItems(i => ({ ...i, [requestId]: res.data }));
      load();
    } catch { setError(t('EvidencePage.errorSubmitLink')); }
  };

  const handleFileSubmit = async (requestId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axiosInstance.post(`/evidence/requests/${requestId}/items/file`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitting(null); setSuccess(t('EvidencePage.successFileUploaded'));
      const res = await axiosInstance.get(`/evidence/requests/${requestId}/items`);
      setItems(i => ({ ...i, [requestId]: res.data }));
      load();
    } catch { setError(t('EvidencePage.errorUploadFile')); }
  };

  const handleReview = async () => {
    if (!reviewModal) return;
    try {
      await axiosInstance.put(`/evidence/items/${reviewModal.itemId}/review`, {
        reviewStatus: reviewModal.status,
        reviewNotes:  reviewModal.notes,
      });
      setReviewModal(null); setSuccess(t('EvidencePage.successReviewSaved'));
      // Reload items for all expanded requests
      for (const reqId of Object.keys(expanded)) {
        if (expanded[reqId]) {
          const res = await axiosInstance.get(`/evidence/requests/${reqId}/items`);
          setItems(i => ({ ...i, [reqId]: res.data }));
        }
      }
      load();
    } catch { setError(t('EvidencePage.errorSaveReview')); }
  };

  const handleDownload = async (itemId, fileName) => {
    try {
      const res = await axiosInstance.get(`/evidence/items/${itemId}/download`,
        { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = fileName || `evidence-${itemId}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { setError(t('EvidencePage.errorDownload')); }
  };

  const summaryCards = [
    { label: t('EvidencePage.summaryRequested'), key: 'requested', color: STATUS_COLORS.REQUESTED, icon: 'fa-inbox' },
    { label: t('EvidencePage.summarySubmitted'), key: 'submitted', color: STATUS_COLORS.SUBMITTED, icon: 'fa-paper-plane' },
    { label: t('EvidencePage.summaryApproved'),  key: 'approved',  color: STATUS_COLORS.APPROVED,  icon: 'fa-check-circle' },
    { label: t('EvidencePage.summaryRejected'),  key: 'rejected',  color: STATUS_COLORS.REJECTED,  icon: 'fa-times-circle' },
  ];

  return (
    <div className="ev-page">
      {/* Header */}
      <div className="ev-header">
        <h2 className="ev-title">{t('EvidencePage.title')}</h2>
        <p className="ev-subtitle">
          {t('EvidencePage.subtitle')}
        </p>
      </div>

      {/* Summary cards */}
      <div className="ev-summary-row">
        {summaryCards.map(c => (
          <div key={c.key} className="ev-summary-card" style={{ borderLeftColor: c.color }}>
            <i className={`fas ${c.icon}`} style={{ color: c.color }} />
            <div>
              <div className="ev-summary-num">{summary[c.key] ?? '—'}</div>
              <div className="ev-summary-label">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {error   && <div className="ev-alert ev-alert-error">{error}<button onClick={() => setError('')}>&times;</button></div>}
      {success && <div className="ev-alert ev-alert-ok">{success}<button onClick={() => setSuccess('')}>&times;</button></div>}

      {/* Tabs (admin sees both views) */}
      {isAdmin && (
        <div className="ev-tabs">
          <button className={`ev-tab${activeTab === 'my'  ? ' active' : ''}`} onClick={() => setActiveTab('my')}>{t('EvidencePage.tabMyRequests')}</button>
          <button className={`ev-tab${activeTab === 'all' ? ' active' : ''}`} onClick={() => setActiveTab('all')}>{t('EvidencePage.tabAllRequests')}</button>
        </div>
      )}

      {/* Request list */}
      {requests.length === 0 ? (
        <div className="ev-empty">
          <i className="fas fa-inbox" />
          <p>{t('EvidencePage.emptyState', { scope: activeTab === 'my' ? t('EvidencePage.emptyScopeMy') : t('EvidencePage.emptyScopeAll') })}</p>
        </div>
      ) : (
        <div className="ev-list">
          {requests.map(req => {
            const reqItems = items[req.id] || [];
            const isOpen   = !!expanded[req.id];
            const isSubmittingThis = submitting === req.id;

            return (
              <div key={req.id} className="ev-card">
                {/* Card header */}
                <div className="ev-card-header" onClick={() => toggleExpand(req.id)}>
                  <div className="ev-card-left">
                    <StatusBadge status={req.status} colors={STATUS_COLORS} />
                    <div className="ev-card-meta">
                      <span className="ev-card-title">{req.title}</span>
                      {req.description && <span className="ev-card-desc">{req.description}</span>}
                    </div>
                  </div>
                  <div className="ev-card-right">
                    {req.dueDate && (
                      <span className="ev-due">
                        <i className="fas fa-calendar-alt" /> {t('EvidencePage.due', { date: req.dueDate })}
                      </span>
                    )}
                    <span className="ev-type-badge">{req.evidenceType}</span>
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ev-chevron`} />
                  </div>
                </div>

                {/* Expanded body */}
                {isOpen && (
                  <div className="ev-card-body">
                    {/* Submitted items */}
                    {reqItems.length > 0 && (
                      <div className="ev-items">
                        <h4>{t('EvidencePage.submittedEvidence', { count: reqItems.length })}</h4>
                        {reqItems.map(item => (
                          <div key={item.id} className="ev-item">
                            <div className="ev-item-left">
                              <i className={`fas ${TYPE_ICONS[item.itemType] || 'fa-file'} ev-item-icon`} />
                              <div className="ev-item-content">
                                {item.itemType === 'FILE' && (
                                  <span className="ev-item-filename">
                                    <button className="ev-link-btn"
                                      onClick={() => handleDownload(item.id, item.fileName)}>
                                      <i className="fas fa-download" /> {item.fileName}
                                    </button>
                                    {item.fileSize && <span className="ev-filesize"> ({Math.round(item.fileSize/1024)} KB)</span>}
                                  </span>
                                )}
                                {item.itemType === 'LINK' && (
                                  <a href={item.externalLink} target="_blank" rel="noreferrer" className="ev-ext-link">
                                    <i className="fas fa-external-link-alt" /> {item.externalLink}
                                  </a>
                                )}
                                {item.notes && <p className="ev-item-notes">{item.notes}</p>}
                                <span className="ev-item-date">
                                  {item.submittedAt ? new Date(item.submittedAt).toLocaleString() : ''}
                                </span>
                              </div>
                            </div>
                            <div className="ev-item-right">
                              <StatusBadge status={item.reviewStatus} colors={REVIEW_COLORS} />
                              {isAdmin && item.reviewStatus === 'PENDING' && (
                                <button className="ev-review-btn"
                                  onClick={() => setReviewModal({ itemId: item.id, status: 'ACCEPTED', notes: '' })}>
                                  {t('EvidencePage.reviewBtn')}
                                </button>
                              )}
                              {item.reviewNotes && (
                                <span className="ev-review-notes" title={item.reviewNotes}>
                                  <i className="fas fa-comment-alt" />
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Submit evidence panel */}
                    {req.status !== 'APPROVED' && (
                      <div className="ev-submit-panel">
                        <h4>{t('EvidencePage.submitEvidence')}</h4>
                        <div className="ev-type-tabs">
                          {['NOTE', 'FILE', 'LINK'].map(type => (
                            <button key={type}
                              className={`ev-type-tab${submitType === type && isSubmittingThis ? ' active' : ''}`}
                              onClick={() => { setSubmitType(type); setSubmitting(req.id); }}>
                              <i className={`fas ${TYPE_ICONS[type]}`} /> {t(`EvidencePage.type_${type}`)}
                            </button>
                          ))}
                        </div>

                        {isSubmittingThis && submitType === 'NOTE' && (
                          <div className="ev-submit-form">
                            <textarea
                              className="ev-textarea"
                              placeholder={t('EvidencePage.notePlaceholder')}
                              value={noteText}
                              onChange={e => setNoteText(e.target.value)}
                              rows={4}
                            />
                            <div className="ev-form-actions">
                              <button className="ev-btn ev-btn-primary"
                                onClick={() => handleNoteSubmit(req.id)}>{t('EvidencePage.submitNoteBtn')}</button>
                              <button className="ev-btn ev-btn-ghost"
                                onClick={() => setSubmitting(null)}>{t('EvidencePage.cancel')}</button>
                            </div>
                          </div>
                        )}

                        {isSubmittingThis && submitType === 'FILE' && (
                          <div className="ev-submit-form">
                            <p className="ev-upload-hint">
                              {t('EvidencePage.uploadHint')}
                            </p>
                            <input type="file" className="ev-file-input"
                              onChange={e => handleFileSubmit(req.id, e.target.files[0])} />
                            <div className="ev-form-actions">
                              <button className="ev-btn ev-btn-ghost"
                                onClick={() => setSubmitting(null)}>{t('EvidencePage.cancel')}</button>
                            </div>
                          </div>
                        )}

                        {isSubmittingThis && submitType === 'LINK' && (
                          <div className="ev-submit-form">
                            <input className="ev-input"
                              placeholder="https://jira.company.com/browse/SEC-123"
                              value={linkUrl}
                              onChange={e => setLinkUrl(e.target.value)} />
                            <textarea className="ev-textarea"
                              placeholder={t('EvidencePage.linkNotePlaceholder')}
                              value={linkNote}
                              onChange={e => setLinkNote(e.target.value)}
                              rows={2} />
                            <div className="ev-form-actions">
                              <button className="ev-btn ev-btn-primary"
                                onClick={() => handleLinkSubmit(req.id)}>{t('EvidencePage.submitLinkBtn')}</button>
                              <button className="ev-btn ev-btn-ghost"
                                onClick={() => setSubmitting(null)}>{t('EvidencePage.cancel')}</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="ev-modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="ev-modal" onClick={e => e.stopPropagation()}>
            <h3>{t('EvidencePage.reviewEvidence')}</h3>
            <div className="ev-modal-field">
              <label>{t('EvidencePage.decision')}</label>
              <div className="ev-radio-group">
                {['ACCEPTED', 'REJECTED'].map(s => (
                  <label key={s} className="ev-radio">
                    <input type="radio" value={s}
                      checked={reviewModal.status === s}
                      onChange={() => setReviewModal(m => ({ ...m, status: s }))} />
                    <span style={{ color: REVIEW_COLORS[s] }}>{t(`EvidencePage.decision_${s}`)}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="ev-modal-field">
              <label>{t('EvidencePage.reviewNotesLabel')}</label>
              <textarea className="ev-textarea"
                rows={3}
                placeholder={t('EvidencePage.reviewNotesPlaceholder')}
                value={reviewModal.notes}
                onChange={e => setReviewModal(m => ({ ...m, notes: e.target.value }))} />
            </div>
            <div className="ev-form-actions">
              <button className="ev-btn ev-btn-primary" onClick={handleReview}>{t('EvidencePage.saveReviewBtn')}</button>
              <button className="ev-btn ev-btn-ghost" onClick={() => setReviewModal(null)}>{t('EvidencePage.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
